import { useEffect, useState } from "react";
import axios from "axios";
import { defaultHeaders } from "../../Redux/Constants/DefaultHeaders.js";
import Pen from "../../Assets/pen.svg";
import {
	Space,
	ConfigProvider,
	Form,
	InputNumber,
	Button,
	Popconfirm,
	notification,
	Input,
	Checkbox,
	TimePicker,
	Switch,
	Modal,
	Select,
} from "antd";
import { CloseCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { logout } from "../../utils/index.js";
import CustomTable from "./CustomTable.js";
import AddressCell from "./AddressCell.js";
dayjs.extend(utc);
dayjs.extend(timezone);

export default function SafeManagement() {
	const [notificationPopup, contextHolder] = notification.useNotification();
	const [form] = Form.useForm();
	const [sortedInfo, setSortedInfo] = useState([]);
	const [filteredInfo, setFilteredInfo] = useState({
		verified: [false],
	});
	const isEditing = (record) => record.key === editingKey;
	const [expandedTableEntries, setExpandedTableEntries] = useState([]);
	const [loading, setLoading] = useState(false);
	const [editingKey, setEditingKey] = useState("");
	const [tableEntries, setTableEntries] = useState([]);
	const [reloadTable, setReloadTable] = useState(false);
	const [expandedParentRowKeys, setExpandedParentRowKeys] = useState([]);
	const [bankingCoreOptions, setBankingCoreOptions] = useState([]);
	const [timePickerValue, setTimePickerValue] = useState(null);
	const [addNewSafe, setAddNewSafe] = useState(false);
	const [safeId, setSafeId] = useState(null);
	const [changedSafeStatus, setChangedSafeStatus] = useState(0);
	const [modalType, setModalType] = useState(null); // "status" | "delete" | null

	useEffect(() => {
		const originData = [];
		const expandedOriginData = [];
		const fetchData = async () => {
			setLoading(true);
			let recordId = null;
			try {
				const safeData = await axios.request({
					method: "GET",
					headers: defaultHeaders(),
					url: `${process.env.REACT_APP_API_URL}api/admin/smart_safe`,
				});
				if (!safeData.data.data) {
					setLoading(false);
				} else {
					for (let data of safeData.data.data.safes) {
						recordId = data.serial_number;
						originData.push({
							key: data.id,
							serialNumber: data.serial_number,
							businessName: data.business_name,
							accountNumber: data.account_number,
							businessLocation: data.business_location,
							contactNumber: data.contact_number,
							status: parseInt(data.status),
							totalBillCount: data.total_bill_count,
							bankingCore: data.banking_core,
							safeName: data.safe_name,
						});
						expandedOriginData.push({
							key: data.id,
							inLockedRoom: data.in_locked_room,
							isNested: data.is_nested,
							isBolted: data.is_bolted,
							isBankOwned: data.is_bank_owned,
							isTethered: data.is_tethered,
							smartSafe: data.smart_safe,
							prefPickupTime: data.preferred_pickup_time,
							amountThreshold: data.amount_threshold
								? `$${data.amount_threshold}.00`
								: null,
							daysThreshold: data.days_threshold,
							envelopeThreshold: data.envelope_threshold,
							billCountThreshold: data.bill_count_threshold,
							timezone: data.timezone,
						});
					}
					setBankingCoreOptions(safeData.data.data.banking_core_options);
					setTableEntries(originData);
					setExpandedTableEntries(expandedOriginData);
					setLoading(false);
				}
			} catch (e) {
				if (e.response.data.status === 401) {
					logout();
				} else {
					notificationPopup.error({
						message: `Safe serial number: ${recordId} encountered the following error`,
						description: e.message,
						placement: "top",
						showProgress: true,
						pauseOnHover: true,
					});
				}
				setLoading(false);
			}
			//Needed so the add safe button work just in case a re render occurs. For some reason, some re renders wont set add new safe to its default value of false
			setAddNewSafe(false);
		};
		fetchData();
	}, [reloadTable, notificationPopup]);

	const removeBlankRow = () => {
		const entries = tableEntries.filter((item) => item.key !== "new entry");
		const expandedEntries = expandedTableEntries.filter(
			(item) => item.key !== "new entry"
		);
		setTableEntries(entries);
		setExpandedTableEntries(expandedEntries);
	};
	const edit = (record) => {
		if (
			tableEntries[0].key === "new entry" &&
			tableEntries[0].serialNumber === ""
		) {
			removeBlankRow();
		}

		// Find the expanded row corresponding to this parent
		const expandedRow =
			expandedTableEntries.find((e) => e.key === record.key) || {};
		form.setFieldsValue({
			...record,
			...expandedRow,
		});
		if (expandedRow.prefPickupTime) {
			setTimePickerValue(dayjs(expandedRow.prefPickupTime, "HH:mm"));
		}

		setEditingKey(record.key);
		setExpandedParentRowKeys([record.key]);
	};
	const cancel = (itemKey = null) => {
		if (itemKey !== null) {
			removeBlankRow();
		}
		setTimePickerValue(null);
		setEditingKey("");
		setAddNewSafe(false);
	};
	const save = async (key) => {
		try {
			const row = await form.validateFields();
			setLoading(true);
			let res;
			// Update parent table
			const updatedParent = tableEntries.map((item) =>
				item.key === key ? { ...item, ...row } : item
			);
			setTableEntries(updatedParent);

			// Update expanded table
			const updatedExpanded = expandedTableEntries.map((item) =>
				item.key === key
					? {
							...item,
							inLockedRoom: row.inLockedRoom,
							isNested: row.isNested,
							isBolted: row.isBolted,
							isBankOwned: row.isBankOwned,
							isTethered: row.isTethered,
							smartSafe: row.smartSafe,
							prefPickupTime: row.prefPickupTime,
							amountThreshold: row.amountThreshold,
							daysThreshold: row.daysThreshold,
							envelopeThreshold: row.envelopeThreshold,
							billCountThreshold: row.billCountThreshold,
					  }
					: item
			);
			setExpandedTableEntries(updatedExpanded);
			const requestData = {
				businessName: row.businessName,
				businessLocation: row.businessLocation,
				serialNumber: row.serialNumber,
				accountNumber: row.accountNumber,
				inLockedRoom: row.inLockedRoom,
				isNested: row.isNested,
				isBolted: row.isBolted,
				isBankOwned: row.isBankOwned,
				isTethered: row.isTethered,
				smartSafe: row.smartSafe,
				prefPickupTime: row.prefPickupTime,
				amountThreshold: row.amountThreshold
					? `$${row.amountThreshold}.00`
					: null,
				daysThreshold: row.daysThreshold,
				envelopeThreshold: row.envelopeThreshold,
				billCountThreshold: row.billCountThreshold,
				timezone: row.timezone,
				bankingCore: row.bankingCore,
				safeName: row.safeName,
				contactNumber: row.contactNumber,
			};
			if (addNewSafe) {
				res = await axios.request({
					method: "Post",
					headers: defaultHeaders(),
					url: `${process.env.REACT_APP_API_URL}api/admin/smart_safe`,
					data: requestData,
				});
			} else {
				res = await axios.request({
					method: "PUT",
					headers: { ...defaultHeaders(), id: key },
					url: `${process.env.REACT_APP_API_URL}api/admin/smart_safe`,
					data: requestData,
				});
			}
			if (!res.data.error) {
				setReloadTable(!reloadTable);
				setEditingKey("");
				setAddNewSafe(false);
			} else {
				notificationPopup.error({
					message: res.data.message,
					placement: "top",
					showProgress: true,
					pauseOnHover: true,
				});
			}
			setLoading(false);
		} catch (errInfo) {
			setLoading(false);
			if (errInfo?.response?.data?.status === 401) {
				logout();
			}
			notificationPopup.error({
				message: "Validation Failed",
				description: JSON.stringify(errInfo?.response?.data?.data),
				placement: "top",
				showProgress: true,
				pauseOnHover: true,
			});
			console.log("Validate Failed:", errInfo);
		}
	};

	const addSafe = () => {
		if (!addNewSafe) {
			const newEntryKey = "new entry";
			const newEntry = {
				key: newEntryKey,
				serialNumber: "",
				businessName: "",
				accountNumber: null,
				businessLocation: "",
				bankingCore: null,
				safeName: "",
			};
			const newExpandedEntry = {
				key: newEntryKey,
				inLockedRoom: false,
				isNested: false,
				isBolted: false,
				isBankOwned: false,
				isTethered: false,
				smartSafe: false,
				prefPickupTime: "09:00",
				amountThreshold: null,
				daysThreshold: null,
				envelopeThreshold: null,
				billCountThreshold: null,
				timezone: null,
			};
			setTimePickerValue(dayjs("09:00", "HH:mm"));
			// Add both table fields
			setTableEntries([newEntry, ...tableEntries]);
			setExpandedTableEntries([newExpandedEntry, ...expandedTableEntries]);

			// Set defaults into form
			form.setFieldsValue({ ...newEntry, ...newExpandedEntry });

			// Start editing and expand the new row
			setEditingKey(newEntryKey);
			setExpandedParentRowKeys([newEntryKey]);
			setAddNewSafe(true);
		}
	};

	const tableColumns = [
		{
			title: "Business Name",
			dataIndex: "businessName",
			key: "businessName",
			editable: true,
			fixed: "left",
			width: 120,
			sortOrder:
				sortedInfo.columnKey === "businessName" ? sortedInfo.order : null,
			sorter: (a, b) => a.businessName.localeCompare(b.businessName),
			align: "center",
			filters: tableEntries
				.map((item) => item.businessName)
				.filter((value, index, self) => self.indexOf(value) === index) // unique names
				.map((name) => ({ text: name, value: name })),

			filterSearch: true,
			filteredValue: filteredInfo.businessName || null,
			onFilter: (value, record) => record.businessName.startsWith(value),
		},
		{
			title: "Safe Name",
			dataIndex: "safeName",
			key: "safeName",
			editable: true,
			sortOrder: sortedInfo.columnKey === "safeName" ? sortedInfo.order : null,
			sorter: (a, b) => a.safeName.localeCompare(b.safeName),
			align: "center",
			fixed: "left",
			width: 50,
			filters: tableEntries
				.map((item) => item.safeName)
				.filter((value, index, self) => self.indexOf(value) === index) // unique names
				.map((name) => ({ text: name, value: name })),

			filterSearch: true,
			filteredValue: filteredInfo.safeName || null,
			onFilter: (value, record) => record.safeName.startsWith(value),
		},
		{
			title: "Serial Number",
			dataIndex: "serialNumber",
			key: "serialNumber",
			editable: true,
			sortOrder:
				sortedInfo.columnKey === "serialNumber" ? sortedInfo.order : null,
			sorter: (a, b) => a.serialNumber.localeCompare(b.serialNumber),
			align: "center",
			filters: tableEntries
				.map((item) => item.serialNumber)
				.filter((value, index, self) => self.indexOf(value) === index) // unique names
				.map((name) => ({ text: name, value: name })),

			filterSearch: true,
			filteredValue: filteredInfo.serialNumber || null,
			onFilter: (value, record) => record.serialNumber.startsWith(value),
			width: 200,
		},
		{
			title: "Account Number",
			dataIndex: "accountNumber",
			key: "accountNumber",
			editable: true,
			sortOrder:
				sortedInfo.columnKey === "accountNumber" ? sortedInfo.order : null,
			sorter: {
				compare: (a, b) => a.accountNumber - b.accountNumber,
			},
			align: "center",
			filters: tableEntries
				.map((item) => item.accountNumber)
				.filter((value, index, self) => self.indexOf(value) === index) // unique names
				.map((name) => ({ text: name, value: name })),

			filterSearch: true,
			filteredValue: filteredInfo.accountNumber || null,
			onFilter: (value, record) => record.accountNumber.startsWith(value),
			width: 180,
		},
		{
			title: "Business Location",
			dataIndex: "businessLocation",
			key: "businessLocation",
			editable: true,
			align: "center",
			filters: tableEntries
				.map((item) => item.businessLocation)
				.filter((value, index, self) => self.indexOf(value) === index) // unique names
				.map((name) => ({ text: name, value: name })),

			filterSearch: true,
			filteredValue: filteredInfo.businessLocation || null,
			onFilter: (value, record) => record.businessLocation.startsWith(value),
		},
		{
			title: "Banking Core",
			dataIndex: "bankingCore",
			key: "bankingCore",
			align: "center",
			editable: true,
			filters: bankingCoreOptions.map((core) => ({
				text: core.name, // what user sees in the filter menu
				value: core.id, // what’s stored in the record
			})),
			filterSearch: true,
			filteredValue: filteredInfo.bankingCore || null,
			onFilter: (value, record) => record.bankingCore === value,
			render: (value) =>
				bankingCoreOptions.find((core) => core.id === value)?.name || value, // show name in table
			width: 100,
		},
		// { Phase 2
		//   title: "Total Bill Count",
		//   dataIndex: "totalBillCount",
		//   key: "Total Bill Count",
		//   sorter: {
		//     compare: (a, b) => a.totalBillCount - b.totalBillCount,
		//   },
		//   align: "center",
		// },
		{
			title: "Phone Number",
			dataIndex: "contactNumber",
			key: "contactNumber",
			align: "center",
			editable: true,
			width: 180,
		},
		{
			title: "Action",
			dataIndex: "action",
			key: "Action",
			width: 100,
			fixed: "right",
			render: (_, record) => {
				return isEditing(record) ? (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "10px",
						}}
					>
						<ConfigProvider
							theme={{
								token: {
									colorBgContainer: "#bcff00",
								},
							}}
						>
							<Button
								onClick={() => save(record.key)}
								style={{
									marginInlineEnd: 8,
								}}
							>
								Save
							</Button>
							<ConfigProvider
								theme={{
									token: {
										colorBgElevated: "#333333",
									},
								}}
							>
								<Popconfirm
									title="Are you sure?"
									onCancel={() => {}}
									cancelText="No"
									okText="Yes"
									onConfirm={() => cancel(record.key)}
									okButtonProps={{ type: "default" }}
								>
									<Button
										style={{
											marginInlineEnd: 8,
										}}
									>
										Cancel
									</Button>
								</Popconfirm>
							</ConfigProvider>
						</ConfigProvider>
					</div>
				) : (
					<Space size="small">
						<button
							style={{ backgroundColor: "transparent" }}
							onClick={() => edit(record)}
						>
							<img src={Pen} alt="Edit Button" />
						</button>
						<Switch
							checkedChildren="On"
							unCheckedChildren="Off"
							onChange={(checked) => {
								console.log(record);
								console.log(checked ? 1 : 0);
								setSafeId(record.key);
								setChangedSafeStatus(checked ? 1 : 0);
								setModalType("status");
							}}
							defaultChecked={record.status === 1}
						/>
						<Button
							type="text"
							icon={<DeleteOutlined style={{ fontSize: "20px" }} />}
							onClick={() => {
								setSafeId(record.key);
								setModalType("delete");
							}}
						/>
					</Space>
				);
			},
			align: "center",
		},
	];
	const expandedTableColumns = [
		{
			title: "In Locked Room",
			dataIndex: "inLockedRoom",
			align: "center",
			key: "inLockedRoom",
			editable: true,
		},
		{
			title: "Inside Another Safe",
			dataIndex: "isNested",
			align: "center",
			key: "isNested",
			editable: true,
		},
		{
			title: "Is Bolted",
			dataIndex: "isBolted",
			key: "isBolted",
			align: "center",
			editable: true,
		},
		{
			title: "Is Bank Owned",
			dataIndex: "isBankOwned",
			key: "isBankOwned",
			align: "center",
			editable: true,
		},
		{
			title: "Is Tethered",
			dataIndex: "isTethered",
			key: "isTethered",
			align: "center",
			editable: true,
		},
		{
			title: "Smart Safe",
			dataIndex: "smartSafe",
			key: "smartSafe",
			align: "center",
			editable: true,
		},
		{
			title: "Preferred Pickup Time",
			dataIndex: "prefPickupTime",
			key: "prefPickupTime",
			align: "center",
			editable: true,
		},
		{
			title: "Amount Threshold",
			dataIndex: "amountThreshold",
			key: "amountThreshold",
			align: "center",
			editable: true,
			width: 100,
		},
		{
			title: "Days Threshold",
			dataIndex: "daysThreshold",
			key: "daysThreshold",
			align: "center",
			editable: true,
			width: 50,
		},
		{
			title: "Envelope Threshold",
			dataIndex: "envelopeThreshold",
			key: "envelopeThreshold",
			align: "center",
			editable: true,
			width: 50,
		},
		{
			title: "Bill Count Threshold",
			dataIndex: "billCountThreshold",
			key: "billCountThreshold",
			align: "center",
			editable: true,
			width: 50,
		},
		{
			title: "Timezone",
			dataIndex: "timezone",
			key: "timezone",
			align: "center",
			editable: true,
		},
	];

	function formatMobileNumber(input) {
		// Remove all non-numeric characters
		const cleaned = ("" + input).replace(/\D/g, "");

		// Split the numbers into area code, first 3 digits, and last 4 digits
		const match = RegExp(/^(\d{0,3})(\d{0,3})(\d{0,4})$/).exec(cleaned);

		if (match) {
			// Format and return the parts
			const formattedNumber = [
				match[1] ? `(${match[1]}` : "",
				match[1] && match[2] ? `) ${match[2]}` : "",
				match[2] && match[3] ? `-${match[3]}` : "",
			].join("");
			return formattedNumber;
		}
		return input;
	}

	const EditableCell = ({
		editing,
		dataIndex,
		title,
		record,
		children,
		isExpandedTable = false,
		...restProps
	}) => {
		const booleanFields = [
			"isNested",
			"isBolted",
			"inLockedRoom",
			"isBankOwned",
			"isTethered",
			"smartSafe",
		];
		const numberFields = [
			"accountNumber",
			"amountThreshold",
			"daysThreshold",
			"envelopeThreshold",
			"billCountThreshold",
		];

		// Fields in the expanded table that are required
		const expandedRequiredFields = [];

		if (!restProps.editable) {
			return <td {...restProps}>{children}</td>;
		}

		let inputNode;
		if (dataIndex === "prefPickupTime") {
			inputNode = (
				<ConfigProvider
					theme={{
						components: {
							DatePicker: {
								cellHoverBg: "#868686",
								cellBgDisabled: "#3b3a3a",
							},
						},
						token: {
							colorBgElevated: "#272727",
							colorIcon: "#868686",
							colorIconHover: "#bcff00",
							controlItemBgActive: "#bcff00",
							colorTextDisabled: "#959595",
							colorTextLightSolid: "#000",
						},
					}}
				>
					<TimePicker
						format="hh:mm A"
						use12Hours
						showNow={false}
						disabled={!record.timezone && !form.getFieldValue("timezone")}
						value={timePickerValue}
						onChange={(time) => {
							if (time) {
								// keep local as dayjs
								setTimePickerValue(time);
								console.log("time", time.format("HH:mm"));
								//extract the selected time in 24h format. No need to do conversion since we should no longer save it in UTC
								form.setFieldValue(dataIndex, time.format("HH:mm"));
							} else {
								setTimePickerValue(null);
								form.setFieldValue(dataIndex, null);
							}
						}}
					/>
				</ConfigProvider>
			);
		} else if (dataIndex === "businessLocation") {
			inputNode = (
				<ConfigProvider
					theme={{
						components: {
							Select: {
								optionActiveBg: "#868686",
							},
						},
						token: {
							fontSize: 16,
							colorBgElevated: "#272727",
						},
					}}
				>
					<AddressCell record={record} dataIndex={dataIndex} form={form} />
				</ConfigProvider>
			);
		} else if (dataIndex === "bankingCore") {
			// Render a Select in edit mode
			inputNode = (
				<ConfigProvider
					theme={{
						components: {
							Select: {
								optionActiveBg: "#868686",
								clearBg: "#FFFFF",
								optionSelectedBg: "#868686",
							},
						},
						token: {
							lineHeight: "1.5714285714285714",
							colorBgElevated: "#272727",
							fontSize: 16,
						},
					}}
				>
					<Select
						style={{ width: "100%" }}
						placeholder="Select banking core"
						options={bankingCoreOptions.map((core) => ({
							label: core.name,
							value: core.id,
						}))}
						defaultValue={
							form.getFieldValue("bankingCore") ??
							bankingCoreOptions.find((c) => c.id === record.bankingCore)
								?.name ??
							null
						}
						onChange={(value, option) => {
							form.setFieldsValue({ bankingCore: value });
						}}
					/>
				</ConfigProvider>
			);
		} else if (booleanFields.includes(dataIndex)) {
			inputNode = <Checkbox />;
		} else if (numberFields.includes(dataIndex)) {
			inputNode = <InputNumber style={{ width: "auto" }} controls={false} />;
		} else if (dataIndex === "timezone") {
			inputNode = (
				<Input.TextArea
					variant="borderless"
					readOnly
					style={{ textAlign: "center" }}
					autoSize
				/>
			);
		} else if (dataIndex === "contactNumber") {
			inputNode = (
				<Input
					autoComplete="on"
					onChange={(e) => {
						const formattedPhoneNumber = formatMobileNumber(e.target.value);
						form.setFieldValue(dataIndex, formattedPhoneNumber);
					}}
					maxLength={14}
					placeholder="Enter a phone number"
					allowClear={{
						clearIcon: (
							<CloseCircleOutlined
								style={{ fontSize: "2vmin", color: "#FFF" }}
							/>
						),
					}}
				/>
			);
		} else {
			inputNode = <Input.TextArea autoSize style={{ padding: "4px" }} />;
		}

		let displayValue = children;
		//Used for boolean values
		if (!editing && booleanFields.includes(dataIndex)) {
			displayValue = record[dataIndex] ? "True" : "False"; // default false
		} else if (!editing && dataIndex === "prefPickupTime") {
			displayValue = record[dataIndex]
				? dayjs(record[dataIndex], "HH:mm")
						.tz(record.timezone ?? form.getFieldValue("timezone"), true) // convert to target timezone
						.format("hh:mm A z")
				: null;
		}
		// Determine validation rules
		let rules = [];
		if (!isExpandedTable) {
			rules.push({ required: true, message: `Please input ${title}!` });
		} else if (expandedRequiredFields.includes(dataIndex)) {
			rules.push({ required: true, message: `Please input ${title}!` });
		}
		return (
			<td {...restProps}>
				{editing ? (
					<Form.Item
						key={`${record.key}-${dataIndex}`}
						name={dataIndex}
						valuePropName={
							booleanFields.includes(dataIndex) ? "checked" : "value"
						}
						style={{
							margin: 10,
						}}
						rules={rules}
						initialValue={record[dataIndex]}
					>
						{inputNode}
					</Form.Item>
				) : (
					displayValue
				)}
			</td>
		);
	};

	const handleConfirm = async () => {
		setLoading(true);
		setModalType(null);
		try {
			if (modalType === "status") {
				await axios.request({
					method: "POST",
					headers: defaultHeaders(),
					url: `${process.env.REACT_APP_API_URL}api/admin/smart_safe/${safeId}`,
					data: { status: changedSafeStatus },
				});
				setChangedSafeStatus(0);
			} else if (modalType === "delete") {
				await axios.request({
					method: "DELETE",
					headers: defaultHeaders(),
					url: `${process.env.REACT_APP_API_URL}api/admin/smart_safe/${safeId}`,
				});
			}
		} catch (err) {
			if (err.response.data.status === 401) {
				logout();
			} else {
				notificationPopup.error({
					message: `Safe serial number: ${safeId} encountered the following error`,
					description: err.message,
					placement: "top",
					showProgress: true,
					pauseOnHover: true,
				});
			}
		} finally {
			setSafeId(null);
			setReloadTable(!reloadTable);
			setLoading(false);
		}
	};

	return (
		<>
			<CustomTable
				tableTitle="Safe Management"
				form={form}
				setEditingKey={setEditingKey}
				reloadTable={reloadTable}
				setReloadTable={setReloadTable}
				tableEntries={tableEntries}
				tableColumns={tableColumns}
				loading={loading}
				expandedTableEntries={expandedTableEntries}
				expandedTableColumns={expandedTableColumns}
				expandedParentRowKeys={expandedParentRowKeys}
				setExpandedParentRowKeys={setExpandedParentRowKeys}
				expandedRowTableKey={(record) => record.key}
				cancel={cancel}
				contextHolder={contextHolder}
				setSortedInfo={setSortedInfo}
				setFilteredInfo={setFilteredInfo}
				EditableCell={EditableCell}
				isEditing={isEditing}
				addBtnText={"Add Safe"}
				addBtnFunction={addSafe}
			/>
			<ConfigProvider
				theme={{
					components: {
						Modal: {
							contentBg: "#565656",
							headerBg: "transparent",
							titleFontSize: "20px",
						},
					},
					token: {
						colorText: "#FFF",
					},
				}}
			>
				<Modal
					open={!!modalType}
					closable={false}
					style={{
						textAlign: "center",
						borderRadius: "24px",
						overflow: "hidden",
						paddingBottom: "0px",
					}}
					title={
						modalType === "status"
							? `Are you sure you want to ${
									changedSafeStatus === 0 ? "disable" : "enable"
							  } this safe?`
							: modalType === "delete"
							? "Are you sure you want to delete this safe?"
							: ""
					}
					zIndex={10000}
					footer={
						<div style={{ textAlign: "center" }}>
							<ConfigProvider
								theme={{
									components: {
										Button: {
											defaultHoverColor: "#161616",
											defaultHoverBg: "#bcff00",
											defaultHoverBorderColor: "#bcff00",
											defaultActiveBorderColor: "#bcff00",
											defaultActiveColor: "#161616",
											defaultActiveBg: "#bcff00",
											defaultBg: "transparent",
											defaultColor: "#FFF",
										},
									},
								}}
							>
								<Button className="modalBtn" onClick={() => setModalType(null)}>
									No
								</Button>
								<Button className="modalBtn" onClick={handleConfirm}>
									Yes
								</Button>
							</ConfigProvider>
						</div>
					}
				/>
			</ConfigProvider>
		</>
	);
}
